import React, { useState, useEffect, useRef, memo } from 'react'
import {
  Dropdown,
  Button,
  Popover,
  Menu,
  Spin,
  Icon,
  Tooltip,
  Progress,
  Modal
} from 'antd'
import VideoPlayer from './VideoPlayer'
import CommentInput from '../../../CommentInput'
import TransitionSelector from '../Transition/TransitionSelector'
import TransitionDisplay from '../Transition/TransitionDisplay'
import VideoHistoryModal from '../VideoHistoryModal'
import VideoEditorModal from './VideoEditorModal'
import {
  DownOutlined,
  ReloadOutlined,
  SettingOutlined,
  EditOutlined,
  VideoCameraOutlined,
  PictureOutlined,
  DeleteOutlined
} from '@ant-design/icons'
import EntitySelectorList from '../../../EntitySelectorList'
import { ImagePreview } from '../../../../../../components'
import ImageHistoryModal from '../../../ImageGeneration/components/ImageHistoryModal'
import SceneTransition from './SceneTransition'

const GeneratingText = ({ text = 'Generating' }) => {
  const [dots, setDots] = useState('')

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => (prev.length >= 3 ? '' : prev + '.'))
    }, 500)
    return () => clearInterval(interval)
  }, [])

  return (
    <div style={{ marginTop: 8, fontWeight: 500 }}>
      {text}
      {dots}
    </div>
  )
}

const Shot = ({
  shot,
  sceneId,
  index,
  isLastShot,
  formData,
  transitions,
  handleRegenerateVideo,
  setScenes,
  handleUploadVideo,
  handleEditSettings,
  handleTransitionSelect,
  isGeneratingAll = false,
  videoGenerating = [],
  videoProvider,
  DragHandle,
  updateShotDataRef,
  // Image generation props
  handleRegenerateImage,
  handleRegenerateWithReferenceNanoBanana,
  isImageGenerationLoading,
  handleDeleteShot
}) => {
  const [isEditMode, setIsEditMode] = useState(false)
  const [hasBeenVisible, setHasBeenVisible] = useState(false)
  const [historyModalVisible, setHistoryModalVisible] = useState(false)
  const [imageHistoryVisible, setImageHistoryVisible] = useState(false)
  const [videoEditorVisible, setVideoEditorVisible] = useState(false)
  const videoWrapperRef = useRef(null)

  const isVideoEditing = !!shot?.videoEditTaskId

  const isVideoGenerating =
    videoGenerating.some(
      status => status.sceneId === sceneId && status.shotId === shot.id
    ) || shot?.isGenerating

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasBeenVisible(true)
        } else {
          setHasBeenVisible(false)
        }
      },
      {
        rootMargin: '200px',
        threshold: 0.01
      }
    )

    if (videoWrapperRef.current) {
      observer.observe(videoWrapperRef.current)
    }

    return () => {
      if (videoWrapperRef.current) {
        observer.unobserve(videoWrapperRef.current)
      }
    }
  }, [])

  // Lazy initialization of video_source_image for legacy shots and backfill history
  useEffect(() => {
    if (shot?.imageSrc && shot?.video_source_image === undefined) {
      const updates = {
        video_source_image: shot.imageSrc
      }

      // Try to backfill source_image in history if it's missing for the current video
      if (shot.vidSrc && shot.videos_history?.length > 0) {
        const historyUpdate = shot.videos_history.map(v => {
          if (v.url === shot.vidSrc && !v.source_image) {
            return { ...v, source_image: shot.imageSrc }
          }
          return v
        })

        // Only update if changes were made
        if (
          JSON.stringify(historyUpdate) !== JSON.stringify(shot.videos_history)
        ) {
          updates.videos_history = historyUpdate
        }
      }

      updateShotDataRef.current(sceneId, shot.id, updates)
    }
  }, [])

  // Auto-regenerate video when switching to video mode if image has changed
  useEffect(() => {
    if (
      !isEditMode &&
      !isVideoGenerating &&
      shot?.imageSrc &&
      shot?.video_source_image &&
      shot.imageSrc !== shot.video_source_image
    ) {
      // Check if we have this video in history
      const existingVideo = shot.videos_history?.find(
        v => v.source_image === shot.imageSrc
      )

      if (existingVideo) {
        // Use existing video from history
        updateShotDataRef.current(sceneId, shot.id, {
          vidSrc: existingVideo.url,
          video_source_image: shot.imageSrc
        })
      } else {
        // Regenerate
        handleRegenerateVideo(sceneId, shot.id)
        updateShotDataRef.current(
          sceneId,
          shot.id,
          'video_source_image',
          shot.imageSrc
        )
      }
    }
  }, [
    isEditMode,
    shot?.imageSrc,
    shot?.video_source_image,
    isVideoGenerating,
    sceneId,
    shot?.id,
    handleRegenerateVideo,
    shot?.videos_history
  ])

  const isError = shot && shot.errorGeneratingVideo

  // Memoize video source to prevent VideoPlayer re-renders
  const videoSrc = shot.vidSrc
  const imageSrc = shot.imageSrc

  return (
    <React.Fragment>
      <div
        className={`shot-card ${isEditMode ? 'is-edit-mode' : ''} ${
          !isVideoGenerating && isError && !isEditMode
            ? 'error-generating-video'
            : ''
        }`}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <h3
            className="shot-title"
            style={{ margin: 0, display: 'flex', alignItems: 'center' }}
          >
            {DragHandle ? <DragHandle /> : null}
            Shot {index + 1}
          </h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div
              className={`mode-toggle-container ${
                isVideoGenerating ? 'disabled' : ''
              }`}
            >
              <div
                className={`mode-toggle-item image ${
                  isEditMode ? 'active' : ''
                }`}
                onClick={() => setIsEditMode(true)}
              >
                <PictureOutlined />
                {isEditMode && 'Image'}
              </div>
              <div
                className={`mode-toggle-item video ${
                  !isEditMode ? 'active' : ''
                }`}
                onClick={() => setIsEditMode(false)}
              >
                <VideoCameraOutlined />
                {!isEditMode && 'Video'}
              </div>
            </div>
          </div>
        </div>

        <div className="shot-card-stack">
          {/* VIDEO LAYER */}
          <div
            className={`shot-card-content video-layer ${
              !isEditMode ? 'active' : 'inactive'
            }`}
          >
            <div
              className="video-wrapper"
              ref={videoWrapperRef}
              style={
                isError
                  ? {
                      border: '1px solid #ff4d4f',
                      position: 'relative'
                    }
                  : { position: 'relative' }
              }
            >
              {videoSrc && !isVideoGenerating ? (
                <VideoPlayer
                  // poster={imageSrc}
                  url={videoSrc}
                  isVisible={hasBeenVisible}
                />
              ) : (
                <img
                  src={imageSrc}
                  width="100%"
                  height="250px"
                  style={{
                    objectFit: 'contain',
                    filter: isError ? 'brightness(0.5)' : 'none'
                  }}
                />
              )}
              {!isVideoGenerating && isError && (
                <div
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: '#fff',
                    border: '1px solid #ff4d4f',
                    color: '#ff4d4f',
                    borderRadius: 4,
                    padding: '2px 6px',
                    fontSize: 12,
                    fontWeight: 600,
                    zIndex: 10
                  }}
                >
                  Task failed. Please regenerate.
                </div>
              )}
              {isVideoGenerating && (
                <div className="video-generating-overlay">
                  <div style={{ textAlign: 'center' }}>
                    <Progress
                      type="circle"
                      percent={shot.progress ? parseInt(shot.progress) : 0}
                      status="active"
                      width={60}
                      strokeColor="#1890ff"
                      trailColor="rgba(255,255,255,0.3)"
                      format={percent => (
                        <span style={{ color: 'white', fontSize: '12px' }}>
                          {percent}%
                        </span>
                      )}
                    />
                    <GeneratingText text="Generating" />
                  </div>
                </div>
              )}
              {isVideoEditing && !isVideoGenerating && (
                <div
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 10,
                    background: '#722ed1',
                    color: '#fff',
                    padding: '4px 10px',
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    boxShadow: '0 2px 8px rgba(114,46,209,0.3)',
                    cursor: 'pointer'
                  }}
                  onClick={() => setVideoEditorVisible(true)}
                >
                  <Spin size="small" style={{ marginRight: 2 }} />
                  Edit in progress...
                </div>
              )}
              {/* Video History Button */}
              {shot.videos_history && shot.videos_history.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    zIndex: 10
                  }}
                >
                  <Tooltip
                    title={`View History (${shot.videos_history.length})`}
                  >
                    <Button
                      shape="round"
                      onClick={() => setHistoryModalVisible(true)}
                      disabled={isVideoGenerating}
                      style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.65)',
                        color: '#fff',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                    >
                      <Icon type="history" />
                    </Button>
                  </Tooltip>
                </div>
              )}
            </div>

            <EntitySelectorList
              activePrompt={shot.video_prompt}
              onUpdatePrompt={newText =>
                updateShotDataRef.current(
                  sceneId,
                  shot.id,
                  'video_prompt',
                  newText
                )
              }
              formData={formData}
              disabled={isVideoGenerating}
            />

            <CommentInput
              characterList={formData.characterList}
              locationReference={formData.locationReference}
              selectedCharacter={formData.selectedCharacter}
              locationMap={formData.locationMap}
              text={shot.video_prompt}
              placeholder="Write your video prompt..."
              disabled={isVideoGenerating}
              onChange={value =>
                updateShotDataRef.current(
                  sceneId,
                  shot.id,
                  'video_prompt',
                  value
                )
              }
            />

            <div
              className="card-actions"
              style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'space-between'
              }}
            >
              <Dropdown
                trigger={['click']}
                overlay={
                  <Menu className="video-generation-menu">
                    <Menu.Item
                      onClick={() => {
                        handleRegenerateVideo(sceneId, shot.id)
                        updateShotDataRef.current(
                          sceneId,
                          shot.id,
                          'video_source_image',
                          shot.imageSrc
                        )
                      }}
                      disabled={isVideoGenerating || isVideoEditing}
                    >
                      <ReloadOutlined /> Regenerate Video
                    </Menu.Item>
                    <Menu.Item onClick={() => setVideoEditorVisible(true)}>
                      <EditOutlined /> Edit Video
                      {isVideoEditing && (
                        <span
                          style={{
                            marginLeft: 8,
                            color: '#722ed1',
                            fontSize: 11
                          }}
                        >
                          (In Progress)
                        </span>
                      )}
                    </Menu.Item>
                  </Menu>
                }
                disabled={isVideoGenerating}
                placement="bottomRight"
              >
                <Button size="small" icon="reload">
                  <span
                    style={{
                      marginLeft: 4,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    Regenerate Video
                  </span>
                  <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
                </Button>
              </Dropdown>
              <Button
                size="small"
                disabled={isVideoGenerating}
                onClick={() => handleEditSettings(sceneId, shot.id)}
              >
                <SettingOutlined />
                <span
                  style={{
                    marginLeft: 4,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Shot Editor
                </span>
              </Button>
            </div>
          </div>

          {/* IMAGE LAYER */}
          <div
            className={`shot-card-content image-layer ${
              isEditMode ? 'active' : 'inactive'
            }`}
          >
            <div
              className="image-wrapper"
              style={
                shot.error
                  ? { border: '1px solid #ff4d4f', position: 'relative' }
                  : { position: 'relative' }
              }
            >
              {shot.isGenerating && shot.progress !== 100 && (
                <div
                  className="progress-overlay"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(0,0,0,0.6)',
                    zIndex: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    borderRadius: '4px'
                  }}
                >
                  <Spin style={{ color: '#fff' }} size="large" />
                  <span className="progress-text" style={{ marginTop: '10px' }}>
                    {shot.progress ? 'Generated Successfully' : 'Generating...'}
                  </span>
                </div>
              )}
              {!shot.isGenerating && shot.error && (
                <div
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: '#fff',
                    border: '1px solid #ff4d4f',
                    color: '#ff4d4f',
                    borderRadius: 4,
                    padding: '2px 6px',
                    fontSize: 12,
                    fontWeight: 600,
                    zIndex: 20
                  }}
                >
                  Task failed. Please regenerate.
                </div>
              )}
              {shot.imageSrc && (
                <ImagePreview
                  src={shot.imageSrc || '/api/placeholder/400/300'}
                  alt={`Shot ${shot.id}`}
                  style={{
                    width: '100%',
                    height: '250px',
                    objectFit: 'contain'
                  }}
                />
              )}

              {/* Image History Button */}
              {shot.images_history && shot.images_history.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    zIndex: 10
                  }}
                >
                  <Tooltip
                    title={`View History (${shot.images_history.length})`}
                  >
                    <Button
                      shape="round"
                      onClick={() => setImageHistoryVisible(true)}
                      disabled={shot.isGenerating}
                      style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.65)',
                        color: '#fff',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                    >
                      <Icon type="history" />
                    </Button>
                  </Tooltip>
                </div>
              )}
            </div>

            <EntitySelectorList
              activePrompt={shot.initial_image_prompt}
              onUpdatePrompt={newText =>
                updateShotDataRef.current(
                  sceneId,
                  shot.id,
                  'initial_image_prompt',
                  newText
                )
              }
              formData={formData}
              disabled={shot.isGenerating}
            />

            <CommentInput
              disabled={shot.isGenerating}
              text={shot.initial_image_prompt}
              placeholder="Write your image prompt..."
              characterList={formData.characterList}
              locationReference={formData.locationReference}
              selectedCharacter={formData.selectedCharacter}
              locationMap={formData.locationMap}
              onChange={value => {
                updateShotDataRef.current(
                  sceneId,
                  shot.id,
                  'initial_image_prompt',
                  value
                )
              }}
            />

            <div
              className="card-actions"
              style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'space-between'
              }}
            >
              <div style={{ display: 'flex', gap: '8px' }}>
                <Dropdown
                  overlay={
                    <Menu>
                      <Menu.Item
                        key="regenerate-image"
                        onClick={() => handleRegenerateImage(sceneId, shot.id)}
                        disabled={shot.isGenerating}
                      >
                        <Icon type="reload" /> Regenerate Image
                      </Menu.Item>
                      <Menu.Item
                        key="regenerate-with-reference-nano-banana"
                        onClick={() =>
                          handleRegenerateWithReferenceNanoBanana(
                            sceneId,
                            shot.id
                          )
                        }
                        disabled={shot.isGenerating}
                      >
                        <Icon type="file-image" /> Edit Image (Multiple
                        References)
                      </Menu.Item>
                    </Menu>
                  }
                  trigger={['click']}
                  placement="bottomLeft"
                >
                  <Button
                    size="small"
                    icon="reload"
                    disabled={shot.isGenerating}
                  >
                    <span
                      style={{
                        marginLeft: 4,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      Regenerate
                    </span>
                    <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
                  </Button>
                </Dropdown>
              </div>

              <Button
                size="small"
                icon="setting"
                onClick={() => handleEditSettings(sceneId, shot.id)}
                disabled={shot.isGenerating}
              >
                <span
                  style={{
                    marginLeft: 4,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Shot Editor
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Only show between-shots transition if it's not the last shot */}
      {/* {!isLastShot && (
        <div className="transition-wrapper">
          <Popover
            content={
              <TransitionSelector
                onSelect={type =>
                  handleTransitionSelect(sceneId, shot.id, type)
                }
                currentTransition={transitions[`${sceneId}-${shot.id}`]}
              />
            }
            trigger="hover"
            placement="right"
          >
            <div className="transition-trigger">
              <TransitionDisplay
                type={transitions[`${sceneId}-${shot.id}`]}
                onRemove={() => handleTransitionSelect(sceneId, shot.id, null)}
              />
            </div>
          </Popover>
        </div>
      )} */}

      {/* {!isLastShot && ( */}
      <SceneTransition
        sceneId={sceneId}
        position={isLastShot ? 'end' : shot.id}
        transitionType={
          transitions[`${sceneId}-${isLastShot ? 'end' : shot.id}`]
        }
        handleTransitionSelect={handleTransitionSelect}
      />
      {/* )} */}

      <VideoHistoryModal
        visible={historyModalVisible}
        history={shot.videos_history || []}
        onCancel={() => setHistoryModalVisible(false)}
        onSelect={url => {
          updateShotDataRef.current(sceneId, shot.id, {
            vidSrc: url,
            video_source_image: shot.imageSrc
          })
          setHistoryModalVisible(false)
        }}
      />

      <ImageHistoryModal
        visible={imageHistoryVisible}
        history={shot.images_history || []}
        onCancel={() => setImageHistoryVisible(false)}
        onSelect={url => {
          updateShotDataRef.current(sceneId, shot.id, 'imageSrc', url)
          setImageHistoryVisible(false)
        }}
      />

      <VideoEditorModal
        visible={videoEditorVisible}
        onCancel={() => setVideoEditorVisible(false)}
        onConfirm={url => {
          updateShotDataRef.current(sceneId, shot.id, {
            vidSrc: url,
            video_source_image: shot.imageSrc,
            videos_history: [
              ...(shot.videos_history || []),
              {
                url: url,
                timestamp: Date.now(),
                duration_at_generation: shot.video_duration,
                source_image: shot.imageSrc
              }
            ],
            videoChatHistory: [],
            videoEditTaskId: null,
            videoEditProgress: 0
          })
          setVideoEditorVisible(false)
        }}
        initialPrompt={shot.video_prompt}
        currentVideo={shot.vidSrc}
        persistedConversation={shot.videoChatHistory || []}
        activeTaskId={shot.videoEditTaskId || null}
        onUpdateState={(history, taskId, progress) => {
          updateShotDataRef.current(sceneId, shot.id, {
            videoChatHistory: history,
            videoEditTaskId: taskId,
            videoEditProgress: progress
          })
        }}
      />
    </React.Fragment>
  )
}

export default Shot
